import { api } from './client';

// Types for Model Pins Management
export interface AdminPinDto {
  username: string;
  position: number;
}

export interface AdminTagPinDto {
  username: string;
  tagSlug: string;
  position: number;
}

export interface AdminPinView {
  id: number;
  username: string;
  position: number;
  modelName?: string;
  imageUrls?: Record<string, string> | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface AdminHomepageView {
  id: number;
  username: string;
  position: number;
  modelName?: string;
  imageUrls?: Record<string, string> | null;
  createdAt?: string;
  updatedAt?: string;
}

// Global Pins API
export const upsertGlobalPin = async (payload: AdminPinDto): Promise<AdminPinView> => {
  const { data } = await api.post<AdminPinView>('/admin/model-pins/global', payload);
  return data;
};

export const deleteGlobalPin = async (username: string): Promise<void> => {
  await api.delete(`/admin/model-pins/global/${encodeURIComponent(username)}`);
};

export const listGlobalPins = async (): Promise<AdminPinView[]> => {
  const { data } = await api.get<AdminPinView[]>('/admin/model-pins/global');
  return data;
};

// Tag Pins API
export const upsertTagPin = async (payload: AdminTagPinDto): Promise<AdminPinView> => {
  const { data } = await api.post<AdminPinView>('/admin/model-pins/tag', payload);
  return data;
};

export const deleteTagPin = async (tagSlug: string, username: string): Promise<void> => {
  await api.delete(`/admin/model-pins/tag/${encodeURIComponent(tagSlug)}/${encodeURIComponent(username)}`);
};

export const listTagPins = async (tagSlug: string): Promise<AdminPinView[]> => {
  const { data } = await api.get<AdminPinView[]>('/admin/model-pins/tag', {
    params: { tag_slug: tagSlug }
  });
  return data;
};

// Homepage Management API
export const upsertHomepage = async (payload: AdminPinDto): Promise<AdminHomepageView> => {
  const { data } = await api.post<AdminHomepageView>('/admin/model-home', payload);
  return data;
};

export const deleteHomepage = async (username: string): Promise<void> => {
  await api.delete(`/admin/model-home/${encodeURIComponent(username)}`);
};

export const listHomepage = async (): Promise<AdminHomepageView[]> => {
  const { data } = await api.get<AdminHomepageView[]>('/admin/model-home');
  return data;
};

// New Homepage API with auto-position and reordering
export interface AdminHomepageAddDto {
  username: string;
}

export interface AdminHomepageReorderDto {
  usernames: string[];
}

export interface AdminHomepageReorderResult {
  updated: number;
  pins: AdminHomepageView[];
}

export const addHomepage = async (payload: AdminHomepageAddDto): Promise<AdminHomepageView> => {
  const { data } = await api.post<AdminHomepageView>('/admin/model-home/add', payload);
  return data;
};

export const reorderHomepage = async (payload: AdminHomepageReorderDto): Promise<AdminHomepageReorderResult> => {
  const { data } = await api.post<AdminHomepageReorderResult>('/admin/model-home/reorder', payload);
  return data;
};

// V4 Page Pins API (Multi-tag support)
export interface AdminPagePinBatchRequest {
  username: string;
  pages: string[][];  // Array of tag arrays, e.g. [["milf"], ["milf", "german"]]
}

export interface AdminPagePinView {
  id: number;
  username: string;
  tags: string[];
  tagsSorted: string;
  priority: number;
  createdAt?: string;
  updatedAt?: string;
}

export const batchAddPagePins = async (payload: AdminPagePinBatchRequest): Promise<{ added: number; message: string }> => {
  const { data } = await api.post<{ added: number; message: string }>('/admin/v4/page-pins/batch-add', payload);
  return data;
};

export const getPagePinsForUser = async (username: string): Promise<{ username: string; pages: string[][] }> => {
  const { data } = await api.get<{ username: string; pages: string[][] }>(`/admin/v4/page-pins/${encodeURIComponent(username)}`);
  return data;
};

export const deletePagePin = async (username: string, tags: string[]): Promise<void> => {
  await api.delete(`/admin/v4/page-pins/${encodeURIComponent(username)}/page`, {
    data: { tags }
  });
};

export const deleteAllPagePinsForUser = async (username: string): Promise<void> => {
  await api.delete(`/admin/v4/page-pins/${encodeURIComponent(username)}`);
};

// Enriched page models (with full model info)
export const getEnrichedPageModels = async (tags: string[]): Promise<{ tags: string[]; models: import('./types').PageModelEnriched[] }> => {
  const { data } = await api.post<{ tags: string[]; models: import('./types').PageModelEnriched[] }>(
    '/admin/v4/page-pins/page-models-enriched', { tags }
  );
  return data;
};

// Set position for model on specific page
export const setPagePinPosition = async (
  username: string, tags: string[], position: number | null
): Promise<void> => {
  await api.put(`/admin/v4/page-pins/${encodeURIComponent(username)}/position`, { tags, position });
};

// Batch set positions for all models on a page
export const batchSetPositions = async (
  tags: string[], positions: { username: string; position: number }[]
): Promise<{ updated: number }> => {
  const { data } = await api.put<{ updated: number }>(
    '/admin/v4/page-pins/batch-position', { tags, positions }
  );
  return data;
};

// Add model to page pin
export const addPagePin = async (username: string, tags: string[]) => {
  const { data } = await api.post('/admin/v4/page-pins', { username, tags });
  return data;
};

// ─────────────────────────────────────────────────────────────
// CPC stats per page — partner sales tool
// ─────────────────────────────────────────────────────────────

export interface CpcCompetitor {
  position: number;
  username: string;
  modelName: string | null;
  imageFilename: string | null;
  pricePerClick: number | null;
  monetizationGroup: string | null;
  ppcType: string | null;
  ppcOrder: number | null;
  source: string | null;
  promote: boolean;
  pinned: boolean;
}

export interface CpcTier {
  positionFrom: number;
  positionTo: number;
  positionRange: string;
  cpc: number | null;
  count: number;
  usernames: string[];
}

export interface CpcStatsResponse {
  tags: string[];
  models: CpcCompetitor[];
  tiers: CpcTier[];
  stats: {
    total: number;
    pinned: number;
    withCpc: number;
    withoutCpc: number;
    minCpc: number | null;
    maxCpc: number | null;
    medianCpc: number | null;
    avgCpc: number | null;
    topBidSuggestion: number | null;
  };
}

export interface CpcBulkRow {
  tags: string[];
  totalModels: number;
  pinnedModels: number;
  withCpc: number;
  minCpc: number | null;
  maxCpc: number | null;
  medianCpc: number | null;
  avgCpc: number | null;
  top1Cpc: number | null;
  top1Username: string | null;
  // OF / OT split — для CPC Overview таблицы.
  countOf: number;
  withCpcOf: number;
  minCpcOf: number | null;
  maxCpcOf: number | null;
  avgCpcOf: number | null;
  countOt: number;
  withCpcOt: number;
  minCpcOt: number | null;
  maxCpcOt: number | null;
  avgCpcOt: number | null;
  // refreshedAt присутствует только когда row пришёл из snapshot cpc_page_stats.
  // Null = live-calc (snapshot для этой страницы ещё не построен).
  refreshedAt?: string | null;
}

export const getCpcStatsForPage = async (tags: string[]): Promise<CpcStatsResponse> => {
  const { data } = await api.post<CpcStatsResponse>('/admin/v4/page-pins/cpc-stats', { tags });
  return data;
};

export const getCpcStatsBulk = async (pages: string[][]): Promise<{ pages: CpcBulkRow[] }> => {
  const { data } = await api.post<{ pages: CpcBulkRow[] }>('/admin/v4/page-pins/cpc-stats/bulk', { pages });
  return data;
};

